import { useCallback, useEffect, useState } from "react";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { commercialInvoiceService } from "../services/commercial-invoice.service";
import type {
  CommercialInvoiceResponseDTO,
  CommercialInvoiceStatus,
} from "../types/commercial-invoice.types";

export interface CommercialInvoiceListFilters {
  status?: CommercialInvoiceStatus;
  date?: string;
}

interface UseCommercialInvoiceListOptions {
  initialPageSize?: number;
}

const DEFAULT_PAGE_SIZE = 10;

export const COMMERCIAL_INVOICE_PAGE_SIZES = [10, 25, 50, 100] as const;

export function useCommercialInvoiceList({
  initialPageSize = DEFAULT_PAGE_SIZE,
}: UseCommercialInvoiceListOptions = {}) {
  const [data, setData] = useState<CommercialInvoiceResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState<CommercialInvoiceListFilters>({});
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    commercialInvoiceService
      .search(
        {
          status: filters.status ? [filters.status] : undefined,
          startDate: filters.date || undefined,
          endDate: filters.date || undefined,
          page,
          size: pageSize,
        },
        { signal: controller.signal },
      )
      .then((response) => {
        if (controller.signal.aborted) return;
        setData(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setError(null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          getApiErrorMessage(
            err,
            "Não foi possível carregar as notas comerciais.",
          ),
        );
        setData([]);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [filters, page, pageSize, reloadToken]);

  const goToPage = useCallback((targetPage: number) => {
    setLoading(true);
    setPage(targetPage);
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    setReloadToken((token) => token + 1);
  }, []);

  const applyFilters = useCallback((next: CommercialInvoiceListFilters) => {
    setLoading(true);
    setPage(0);
    setFilters(next);
  }, []);

  const setPageSize = useCallback((size: number) => {
    setLoading(true);
    setPage(0);
    setPageSizeState(size);
  }, []);

  const updateLocal = useCallback((updated: CommercialInvoiceResponseDTO) => {
    setData((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item)),
    );
  }, []);

  return {
    data,
    loading,
    error,
    page,
    pageSize,
    totalPages,
    totalElements,
    filters,
    setFilters: applyFilters,
    setPage: goToPage,
    setPageSize,
    reload,
    updateLocal,
  };
}
